import * as React from 'react'

import { cn } from '@ui/lib/utils'

interface AppShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optionaler Sidebar-Inhalt. Fehlt er, entfällt die Seitenspalte. */
  sidebar?: React.ReactNode
  /** Optionaler Header-Inhalt (Titelzeile, Suche, Avatar …). */
  header?: React.ReactNode
}

/**
 * AppShell — Layout-Primitive des Frontstage Kits.
 *
 * Rahmt jede App: Der optionale Header spannt sich über die gesamte Breite.
 * Darunter stehen die optionale Sidebar und der Content-Bereich nebeneinander.
 * Responsive blendet sich die Sidebar unterhalb von `md` aus.
 *
 * Farben, Radien und Schriften kommen ausschließlich aus den Theme-Tokens
 * (theme.css) — keine Hex-Werte, keine Inline-Styles.
 *
 * Abweichung vom Kit-Original: Der Header ist 48px hoch statt 56px (`h-12`
 * statt `h-14`), mit entsprechend engerem Padding. Diese Plattform gibt den
 * Platz lieber den Fachmodulen; die Kopfzeile trägt nur Navigation, Name,
 * Suche und Nutzermenü. Bitte ans Kit zurückmelden.
 *
 * Ohne `sidebar` entfällt die Seitenspalte vollständig — nicht nur optisch.
 * Der Header behält dadurch auf allen Routen dieselbe Position und Geometrie.
 */
export function AppShell({
  sidebar,
  header,
  className,
  children,
  ...props
}: AppShellProps) {
  return (
    <div className="flex min-h-svh flex-col bg-background text-foreground [--berky-shell-header-height:3rem]">
      {header && (
        <header className="relative z-30 flex h-[var(--berky-shell-header-height)] shrink-0 items-center gap-3 border-b bg-card px-3">
          {header}
        </header>
      )}
      <div className="flex min-h-0 flex-1">
        {sidebar && (
          <aside className="hidden w-60 shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground md:flex">
            {sidebar}
          </aside>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <main
            className={cn(
              'mx-auto w-full max-w-6xl flex-1 [--berky-shell-content-padding:1rem] p-[var(--berky-shell-content-padding)] md:[--berky-shell-content-padding:1.5rem]',
              className,
            )}
            {...props}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
